import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { User, LogOut, ChevronDown } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'

/**
 * Profile pill showing the user's first name and a user icon.
 *
 * Desktop: clicking the pill opens a dropdown with links to the profile page
 * and a logout action.
 * Mobile: the pill itself links directly to the profile page.
 */
export default function ProfilePill({ user }) {
  const { t } = useTranslation()
  const displayName = user?.firstName || user?.f_name || t('common.userFallback', 'Użytkownik')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleLogout = async () => {
    setIsOpen(false)
    await logout()
    navigate('/')
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Mobile: direct link, no dropdown */}
      <Link
        to="/moja-biblioteka/profil"
        className="lg:hidden flex items-center gap-2 sm:gap-3 bg-[var(--library-card-bg)] backdrop-blur-md rounded-full pl-4 pr-2 sm:pr-3 py-2 shadow-lg border border-[var(--library-border)] hover:bg-white/40 transition"
      >
        <span className="font-bold text-brand-blue text-sm sm:text-base">
          {displayName}
        </span>
        <span className="bg-brand-blue text-brand-gold rounded-full p-1.5">
          <User size={18} aria-hidden="true" />
        </span>
      </Link>

      {/* Desktop: dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="hidden lg:flex items-center gap-2 sm:gap-3 bg-[var(--library-card-bg)] backdrop-blur-md rounded-full pl-4 pr-2 sm:pr-3 py-2 shadow-lg border border-[var(--library-border)] hover:bg-white/40 transition"
      >
        <span className="font-bold text-brand-blue text-sm sm:text-base">
          {displayName}
        </span>
        <span className="bg-brand-blue text-brand-gold rounded-full p-1.5">
          <User size={18} aria-hidden="true" />
        </span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`text-brand-blue transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Desktop dropdown */}
      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-48 bg-[var(--library-card-bg)] backdrop-blur-md rounded-2xl shadow-xl border border-[var(--library-border)] overflow-hidden z-50"
        >
          {user?.isAdmin && (
            <a
              href="/admin/dashboard"
              onClick={() => setIsOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-3 text-brand-blue hover:bg-white/40 transition"
            >
              <span className="font-bold text-sm">{t('nav.adminPanel')}</span>
            </a>
          )}
          <Link
            to="/moja-biblioteka/profil"
            onClick={() => setIsOpen(false)}
            role="menuitem"
            className="flex items-center gap-3 px-4 py-3 text-brand-blue hover:bg-white/40 transition"
          >
            <User size={18} aria-hidden="true" />
            <span className="font-bold text-sm">{t('nav.profile')}</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            role="menuitem"
            className="w-full flex items-center gap-3 px-4 py-3 text-brand-blue hover:bg-white/40 transition text-left"
          >
            <LogOut size={18} aria-hidden="true" />
            <span className="font-bold text-sm">{t('nav.logout')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
